// Where a local recording's bytes go, and in what format.
//
// The rule that shapes this file: NEVER hold the recording in memory. The old
// implementation pushed every chunk into a JS array and built one Blob at the
// end, so an hour of 1080p — gigabytes — had to fit in the tab. It didn't.
//
// Chunks are handed to a sink as they arrive and are gone from the heap
// immediately, so memory stays flat whether the meeting runs ten minutes or
// three hours.

import { isEmbedded } from '@/features/embed/embed-bridge'

/** A place to stream recording chunks to, one at a time. */
export type RecordingSink = {
  /** Human-readable destination, for the UI ("Downloads/meeting-….mp4"). */
  readonly name: string
  write(chunk: Blob): Promise<void>
  /** Flush and finish. Returns a URL only for sinks that must hand back a Blob. */
  close(): Promise<{ downloadUrl?: string }>
  /** Give up without producing a file (start failed, user cancelled). */
  abort(): Promise<void>
}

type FilePickerOptions = {
  suggestedName?: string
  types?: { description: string; accept: Record<string, string[]> }[]
}
type WritableStreamLike = {
  write(data: unknown): Promise<void>
  close(): Promise<void>
  abort(): Promise<void>
  // mp4-muxer seeks back to write the moov once sizes are known.
  seek?(position: number): Promise<void>
}
type FileHandleLike = { name: string; createWritable(): Promise<WritableStreamLike> }
type PickerWindow = Window & { showSaveFilePicker?: (o?: FilePickerOptions) => Promise<FileHandleLike> }

export function supportsFileStreaming(): boolean {
  // Chrome exposes the picker inside a cross-origin frame but throws
  // SecurityError when it is called, which the recorder reads as the user
  // cancelling — so an embedded meeting's local recording did nothing at all.
  // In a frame, take the fallback path the browsers without a picker use.
  if (isEmbedded()) return false
  return typeof (window as PickerWindow).showSaveFilePicker === 'function'
}

/**
 * Chrome/Edge: the user picks the destination once and every chunk is written
 * straight to that file. Constant memory, and the recording survives a tab
 * crash up to the last flushed chunk.
 */
async function createFileSystemSink(suggestedName: string, mimeType: string): Promise<RecordingSink> {
  const ext = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
  const handle = await (window as PickerWindow).showSaveFilePicker!({
    suggestedName: `${suggestedName}.${ext}`,
    types: [{ description: 'Video', accept: { [mimeType.split(';')[0]]: [`.${ext}`] } }],
  })
  const writable = await handle.createWritable()
  let closed = false
  return {
    name: handle.name,
    async write(chunk) {
      if (closed) return
      await writable.write(chunk)
    },
    async close() {
      if (closed) return {}
      closed = true
      await writable.close()
      return {}
    },
    async abort() {
      if (closed) return
      closed = true
      try { await writable.abort() } catch { /* already gone */ }
    },
  }
}

const DB_NAME = 'inmeet_recordings'
const STORE = 'chunks'

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { autoIncrement: true })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function tx<T>(db: IDBDatabase, mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = fn(db.transaction(STORE, mode).objectStore(STORE))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Firefox/Safari fallback: one IndexedDB record per chunk, assembled only at the
 * end. Slower than a direct file write, and the final Blob does briefly exist in
 * memory — but during the recording (the long part, where the old code died) the
 * heap stays flat, and IndexedDB is backed by disk rather than the JS heap.
 */
async function createIndexedDbSink(suggestedName: string, mimeType: string): Promise<RecordingSink> {
  const ext = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
  const fileName = `${suggestedName}.${ext}`
  const db = await openDb()
  // A previous session that crashed mid-record would otherwise leak its chunks
  // into this one's output.
  await tx(db, 'readwrite', (s) => s.clear())

  let closed = false
  const cleanup = async () => {
    try { await tx(db, 'readwrite', (s) => s.clear()) } catch { /* best effort */ }
    db.close()
  }

  return {
    name: fileName,
    async write(chunk) {
      if (closed) return
      await tx(db, 'readwrite', (s) => s.add(chunk))
    },
    async close() {
      if (closed) return {}
      closed = true
      const chunks = await tx<Blob[]>(db, 'readonly', (s) => s.getAll() as IDBRequest<Blob[]>)
      const url = URL.createObjectURL(new Blob(chunks, { type: mimeType }))
      await cleanup()
      return { downloadUrl: url }
    },
    async abort() {
      if (closed) return
      closed = true
      await cleanup()
    },
  }
}

export async function createRecordingSink(suggestedName: string, mimeType: string): Promise<RecordingSink> {
  if (supportsFileStreaming()) return createFileSystemSink(suggestedName, mimeType)
  return createIndexedDbSink(suggestedName, mimeType)
}

/**
 * A raw file stream for the WebCodecs path.
 *
 * mp4-muxer writes into the handle itself (seeking back to patch the `moov`),
 * so it needs the real `FileSystemWritableFileStream` rather than the wrapped
 * append-only sink above. Returns null when the browser has no File System
 * Access API — the caller then buffers and downloads instead.
 */
export async function createMp4FileStream(
  suggestedName: string,
): Promise<{ stream: WritableStreamLike; name: string } | null> {
  if (!supportsFileStreaming()) return null
  const handle = await (window as PickerWindow).showSaveFilePicker!({
    suggestedName: `${suggestedName}.mp4`,
    types: [{ description: 'Video', accept: { 'video/mp4': ['.mp4'] } }],
  })
  return { stream: await handle.createWritable(), name: handle.name }
}

/**
 * Codec candidates, cheapest encode first.
 *
 * H.264 is hardware-accelerated on effectively all current hardware; VP8 is a
 * light software encode; VP9 is a heavy one. The machine is already decoding
 * every incoming stream, so an encoder choice that lands in software costs the
 * call itself — which is why the old hardcoded VP9 was the wrong default.
 */
const CANDIDATES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
]

/** First supported container/codec, or null when MediaRecorder can't help us. */
export function pickRecordingMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  for (const type of CANDIDATES) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }
  return null
}

/**
 * True when we had to fall back to WebM.
 *
 * Worth surfacing, because Windows' built-in players (Movies & TV / Media
 * Player) have poor-to-no WebM support: a file that plays perfectly in Chrome or
 * VLC can show no audio, stutter, or refuse to seek there. MediaRecorder's WebM
 * is also written as a stream — no Cues index and no duration in the header — so
 * even players that do decode it often cannot seek it.
 */
export function isFallbackWebm(mimeType: string): boolean {
  return mimeType.startsWith('video/webm')
}

/** Every candidate this browser supports — for diagnosing playback reports. */
export function supportedRecordingTypes(): string[] {
  if (typeof MediaRecorder === 'undefined') return []
  return CANDIDATES.filter((t) => MediaRecorder.isTypeSupported(t))
}

/** `meeting-recording-2026-08-04T10-15-00` — safe on every filesystem. */
export function defaultRecordingName(now: Date): string {
  return `meeting-recording-${now.toISOString().slice(0, 19).replace(/:/g, '-')}`
}
